<script setup>
import { Head, useForm } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import VariantPageWrapper from '@/Components/VariantPageWrapper.vue';

const props = defineProps({
    domain: Object,
    status: { type: String, default: null },
    matches: { type: Number, default: 0 },
});

const form = useForm({
    email: '',
    phone: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
});

const submit = () => {
    form.post('/widerruf', {
        preserveScroll: true,
        onSuccess: () => {
            form.reset();
        },
    });
};
</script>

<template>
    <Head title="Widerruf / Datenlöschung">
        <meta name="description" content="Widerrufsrecht und Datenlöschung - jetzt Anfrage widerrufen und Ihre Daten anonymisieren lassen." />
    </Head>

    <AppLayout :domain="domain" heroMode :hideChrome="!!domain?.settings?.design_variant">
        <VariantPageWrapper :domain="domain">
        <section class="relative" style="background: var(--dv-page);">
            <div class="max-w-3xl mx-auto px-6 lg:px-10 pt-24 md:pt-32 pb-10 text-center">
                <div class="eyebrow mb-6">Widerruf</div>
                <h1 class="text-[clamp(36px,5vw,56px)] leading-[1.1]" style="font-family: var(--dv-font-heading); color: var(--dv-text); font-weight: 500; letter-spacing: -0.025em;">
                    Anfrage widerrufen
                </h1>
                <p class="mt-6 max-w-xl mx-auto text-[17px] leading-[1.65]" style="color: var(--dv-muted);">
                    Sie möchten Ihre Anfrage zurückziehen und Ihre Daten löschen lassen?
                    Bestätigen Sie Ihre Identität mit den Angaben aus Ihrer ursprünglichen Anfrage.
                </p>
            </div>

            <div class="max-w-xl mx-auto px-6 lg:px-10 pb-24">
                <!-- Success -->
                <div v-if="status === 'success'" class="p-8" style="background: #ecfdf5; border: 1px solid #6ee7b7; border-radius: 6px;">
                    <h2 class="text-[22px] mb-3" style="font-family: var(--dv-font-heading); font-weight: 500; color: #065f46;">Widerruf bestätigt</h2>
                    <p class="text-[15px] leading-[1.7]" style="color: #065f46;">
                        <strong>{{ matches }}</strong> Anfrage{{ matches === 1 ? '' : 'n' }} wurden erfolgreich widerrufen.
                        Alle personenbezogenen Daten wurden auf "Gelöscht vom Kunden" gesetzt.
                        Eine weitere Kontaktaufnahme durch Fachbetriebe erfolgt nicht mehr.
                    </p>
                </div>

                <!-- Not found -->
                <div v-else-if="status === 'not_found'" class="p-6 mb-6" style="background: #fef3c7; border: 1px solid #fcd34d; border-radius: 6px;">
                    <p class="text-[14px] leading-[1.6]" style="color: #92400e;">
                        Wir konnten keine Anfrage mit diesen Angaben finden.
                        Bitte überprüfen Sie E-Mail, Telefonnummer und Straße und versuchen Sie es erneut,
                        oder kontaktieren Sie uns direkt.
                    </p>
                </div>

                <!-- Form -->
                <form v-if="status !== 'success'" @submit.prevent="submit" class="p-6 md:p-8" style="background: var(--dv-card); border: 1px solid var(--dv-border); border-radius: 6px;">
                    <div class="eyebrow mb-3">Identität bestätigen</div>
                    <h2 class="text-[22px] mb-6" style="font-family: var(--dv-font-heading); font-weight: 500; letter-spacing: -0.02em; color: var(--dv-text);">
                        Ihre Kontaktdaten zur Bestätigung
                    </h2>

                    <div class="space-y-4">
                        <div>
                            <label for="wr-email" class="block text-[13px] font-semibold mb-1.5" style="color: var(--dv-text);">E-Mail-Adresse</label>
                            <input id="wr-email" v-model="form.email" type="email" required autocomplete="email" placeholder="max.mustermann@beispiel.de" class="w-full px-4 py-3 text-[15px] rounded-md focus:outline-none" style="border: 1px solid var(--dv-border); min-height: 48px; background: var(--dv-page); color: var(--dv-text);" />
                            <p v-if="form.errors.email" class="mt-1 text-[12px]" style="color: #dc2626;">{{ form.errors.email }}</p>
                        </div>

                        <div>
                            <label for="wr-phone" class="block text-[13px] font-semibold mb-1.5" style="color: var(--dv-text);">Telefonnummer</label>
                            <input id="wr-phone" v-model="form.phone" type="tel" required autocomplete="tel" placeholder="0151 12345678" class="w-full px-4 py-3 text-[15px] rounded-md focus:outline-none" style="border: 1px solid var(--dv-border); min-height: 48px; background: var(--dv-page); color: var(--dv-text);" />
                            <p v-if="form.errors.phone" class="mt-1 text-[12px]" style="color: #dc2626;">{{ form.errors.phone }}</p>
                        </div>

                        <div class="grid grid-cols-3 gap-3">
                            <div class="col-span-2">
                                <label for="wr-street" class="block text-[13px] font-semibold mb-1.5" style="color: var(--dv-text);">Straße</label>
                                <input id="wr-street" v-model="form.street" type="text" required autocomplete="street-address" placeholder="Musterstraße" class="w-full px-4 py-3 text-[15px] rounded-md focus:outline-none" style="border: 1px solid var(--dv-border); min-height: 48px; background: var(--dv-page); color: var(--dv-text);" />
                            </div>
                            <div>
                                <label for="wr-housenr" class="block text-[13px] font-semibold mb-1.5" style="color: var(--dv-text);">Nr.</label>
                                <input id="wr-housenr" v-model="form.house_number" type="text" placeholder="12" class="w-full px-4 py-3 text-[15px] rounded-md focus:outline-none" style="border: 1px solid var(--dv-border); min-height: 48px; background: var(--dv-page); color: var(--dv-text);" />
                            </div>
                        </div>
                        <p v-if="form.errors.street" class="mt-1 text-[12px]" style="color: #dc2626;">{{ form.errors.street }}</p>

                        <div class="grid grid-cols-3 gap-3">
                            <div>
                                <label for="wr-plz" class="block text-[13px] font-semibold mb-1.5" style="color: var(--dv-text);">PLZ</label>
                                <input id="wr-plz" v-model="form.postal_code" type="text" inputmode="numeric" maxlength="5" placeholder="10115" class="w-full px-4 py-3 text-[15px] rounded-md focus:outline-none" style="border: 1px solid var(--dv-border); min-height: 48px; background: var(--dv-page); color: var(--dv-text);" />
                            </div>
                            <div class="col-span-2">
                                <label for="wr-city" class="block text-[13px] font-semibold mb-1.5" style="color: var(--dv-text);">Ort</label>
                                <input id="wr-city" v-model="form.city" type="text" autocomplete="address-level2" placeholder="Berlin" class="w-full px-4 py-3 text-[15px] rounded-md focus:outline-none" style="border: 1px solid var(--dv-border); min-height: 48px; background: var(--dv-page); color: var(--dv-text);" />
                            </div>
                        </div>
                    </div>

                    <button type="submit" :disabled="form.processing" class="mt-6 w-full inline-flex items-center justify-center px-6 h-[56px] text-[16px] font-medium" style="background: var(--brand-accent); color: var(--brand-accent-text, #fff); border-radius: 4px;">
                        {{ form.processing ? 'Wird geprüft...' : 'Daten widerrufen' }}
                    </button>

                    <p class="mt-4 text-[12px] leading-[1.6]" style="color: var(--dv-muted);">
                        Nach erfolgreicher Prüfung werden alle personenbezogenen Daten in unserer Datenbank
                        auf "Gelöscht vom Kunden" gesetzt und nicht mehr an Fachbetriebe weitergegeben.
                        Dieser Vorgang ist unwiderruflich.
                    </p>
                </form>

                <div class="mt-8 text-center text-[13px]" style="color: var(--dv-muted);">
                    Fragen?
                    <a href="/kontakt" style="color: var(--brand-accent-fg, var(--brand-accent)); border-bottom: 1px solid currentColor;">Kontaktieren Sie uns</a>
                </div>
            </div>
        </section>
        </VariantPageWrapper>
    </AppLayout>
</template>
